import {
  getCurrentUTCDateTime,
  getUnixTimestampSeconds,
  getMomentTime
} from 'utils/time'
import { RESPONSE_STATES, QUESTION_TYPES } from 'utils/constants'
import { ICONS } from '../../Activities/utils/PropertyFields'
import { resolveActiveInstanceIdForInstrument } from '../../Activities/utils/instrumentUtils'

/** Types `WebSurveyPage` / `Sibling` can render; others are ignored on the page. */
const WEB_SURVEY_RENDERABLE_QUESTION_TYPES = new Set([
  ...Object.values(QUESTION_TYPES),
  'score-summary', // synthetic end screen from WebSurvey module, not in QUESTION_TYPES
])

const REVIEW_EXCLUDED_TYPES = new Set([
  'introduction',
  'prompt',
  'review',
  'score-summary',
])

// GOLD-12178 / GOLD-12260: TMG is on Review only when in_review is
// explicitly true. Other types default to true when the flag is omitted
// so existing instruments keep showing on Review. Mixed Select One +
// TMG (review off) still lists Select One only.
export const isQuestionIncludedInReview = (question = {}) => {
  const type = question?.type || ''
  if (!type || REVIEW_EXCLUDED_TYPES.has(type)) return false
  if (type === QUESTION_TYPES.timedMatchingGrid) {
    const inReview =
      question?.attributes?.in_review ??
      question?.attributes?.timed_matching_grid_details?.in_review
    return inReview === true
  }
  const inReview = question?.attributes?.in_review
  return inReview === undefined ? true : !!inReview
}

// GOLD-12260: True when at least one question would appear on Review.
// Used to skip an empty Review for TMG-only instruments with review off.
export const instrumentHasReviewableQuestions = (questions = {}) =>
  Object.values(questions || {}).some(isQuestionIncludedInReview)

// GOLD-12260: After the last real question, go to Review only if
// something reviewable exists. TMG-only with in_review off goes to
// finish (or score-summary). Do not remap 'review' → submit just because
// the last question is TMG — mixed instruments still need Review.
export const getReviewOrFinishDestination = ({
  reviewEnabled,
  questions = {},
  hasScoreSummary = false,
} = {}) => {
  if (reviewEnabled && instrumentHasReviewableQuestions(questions)) {
    return 'review'
  }
  return hasScoreSummary ? 'score-summary' : 'submit-id'
}

/**
 * Walk linear `nextQuestionId` chain until the next id is missing from `questions`
 * or is a type this web app renders. Prevents re-entering a sibling group when the
 * immediate next row is an unsupported type (e.g. food_entry) still in siblings JSON.
 */
export const getNextRenderableQuestionId = (
  webSurvey,
  completedQuestionId,
  nextQuestionId,
) => {
  const { questions } = webSurvey
  let nextId = nextQuestionId(completedQuestionId)
  while (
    nextId &&
    questions[nextId] &&
    !WEB_SURVEY_RENDERABLE_QUESTION_TYPES.has(questions[nextId].type)
  ) {
    nextId = nextQuestionId(nextId)
  }
  return nextId
}

export const capitalize = (string) =>
  string.charAt(0) + string.slice(1).toLowerCase()

const choiceValueCandidate = (raw) => {
  if (raw === undefined || raw === null) return ''
  return String(raw)
}

const stripHtmlText = (raw) =>
  String(raw ?? '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&ldquo;|&rdquo;|&quot;/gi, '"')
    .replace(/&#39;|&rsquo;/gi, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const choiceMatchTokens = (choice) => {
  const tokens = []
  const add = (raw) => {
    if (raw === undefined || raw === null || raw === '') return
    const str = String(raw)
    tokens.push(str)
    const stripped = stripHtmlText(str)
    if (stripped && stripped !== str) tokens.push(stripped)
  }
  add(choice?.value)
  add(choice?.choice_value)
  add(choice?.text)
  add(choice?.title)
  add(choice?.label)
  return tokens
}

/** Submission `value` for a choice answer (not the choice id). */
export const getChoiceAnswerValue = (choices, choiceId, fallback = choiceId) => {
  const choice = choices?.[choiceId]
  if (!choice) return fallback

  const idStr = String(choiceId)
  const candidates = [
    choice.value,
    choice.choice_value,
    choice.text,
    choice.title,
    choice.label,
  ]

  for (const candidate of candidates) {
    const str = choiceValueCandidate(candidate)
    if (str !== '' && str !== idStr) return str
  }

  return fallback
}

/** Ensure final submit payload uses authored choice value, not choice id. */
export const normalizeChoiceAnswerForSubmit = (authoredQuestion = {}, answerItem = {}) => {
  if (!answerItem || typeof answerItem !== 'object') return answerItem
  const choices = authoredQuestion?.choices
  if (!choices) return answerItem

  const choiceId =
    answerItem.choice_id ||
    answerItem.id ||
    resolveChoiceIdFromStoredAnswer(choices, answerItem)
  if (!choiceId) return answerItem

  let fallback = answerItem.value
  if (
    authoredQuestion?.type === 'likert' &&
    Array.isArray(authoredQuestion?.choices_order)
  ) {
    const idx = authoredQuestion.choices_order.indexOf(choiceId)
    if (idx >= 0) fallback = `${idx + 1}`
  }

  const value = getChoiceAnswerValue(choices, choiceId, fallback)
  const { choice_id: _cid, id: _id, ...rest } = answerItem
  return { ...rest, value, choice_id: choiceId }
}

/** Restore UI selection from stored answer (supports legacy id-in-value payloads). */
export const resolveChoiceIdFromStoredAnswer = (choices, answerItem = {}) => {
  if (!choices || !answerItem) return null
  const { choice_id: choiceId, id: answerId, value, label } = answerItem
  if (choiceId && choices[choiceId]) return choiceId
  if (answerId && choices[answerId]) return answerId

  const candidates = [value, label]
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') {
      continue
    }
    const valueStr = String(candidate)
    if (choices[valueStr]) return valueStr
    const strippedVal = stripHtmlText(valueStr)
    const match = Object.keys(choices).find((id) => {
      const tokens = choiceMatchTokens(choices[id])
      return (
        tokens.includes(valueStr) ||
        (strippedVal !== '' && tokens.includes(strippedVal))
      )
    })
    if (match) return match
  }
  return null
}

export const getIcon = (key) =>
  ICONS.find((item) => item.key === capitalize(key) || item.key === 'General')
    .icon

export const formatSubmissionSurvey = (instrument, complete = false) => {
  const submission = JSON.parse(JSON.stringify(instrument))

  submission.state = complete ? 'COMPLETE' : 'INCOMPLETE'
  submission.start = getCurrentUTCDateTime()

  const instanceId =
    instrument?.instance_id || resolveActiveInstanceIdForInstrument(instrument)
  const matchedInstance = instanceId
    ? (instrument?.instances || []).find(
        (instance) =>
          String(instance?.instance_id) === String(instanceId),
      )
    : null
  const scheduleId =
    instrument?.schedule_id ||
    matchedInstance?.schedule_id ||
    matchedInstance?.scheduleId ||
    instrument?.instances?.[0]?.schedule_id ||
    instrument?.instances?.[0]?.scheduleId

  if (instanceId) submission.instance_id = instanceId
  if (scheduleId) submission.schedule_id = scheduleId

  delete submission.url
  return submission
}

const orderQuestionMapByInstrument = (byId, order0 = []) => {
  const out = []
  const seen = new Set()
  for (const qid of order0) {
    if (byId.has(qid)) {
      out.push(byId.get(qid))
      seen.add(qid)
    }
  }
  byId.forEach((q, id) => {
    if (!seen.has(id)) out.push(q)
  })
  return out
}

/**
 * Merge Save & Resume localStorage into template submission (GOLD-11478).
 * Re-orders questions to match instrument order so Review stays consistent.
 */
const toQuestionsArray = (questions) => {
  if (Array.isArray(questions) && questions.length) return questions
  if (questions && typeof questions === 'object')
    return Object.values(questions)
  return []
}

export const mergeStoredSubmissionWithBase = (base, stored, order0 = []) => {
  const baseQs = toQuestionsArray(base?.questions)
  const byId = new Map()
  const mergeOne = (q) => {
    if (!q?.id) return
    const prev = byId.get(q.id)
    if (!prev) {
      byId.set(q.id, { ...q })
      return
    }
    const pLen = Array.isArray(prev.answer) ? prev.answer.length : 0
    const nLen = Array.isArray(q.answer) ? q.answer.length : 0
    byId.set(q.id, {
      ...prev,
      ...q,
      answer: nLen >= pLen ? (q.answer ?? prev.answer) : prev.answer,
    })
  }
  baseQs.forEach(mergeOne)

  const storedQs = toQuestionsArray(stored?.questions)
  if (
    storedQs.length &&
    (!base?.instance_id ||
      !stored.instance_id ||
      String(base.instance_id) === String(stored.instance_id))
  ) {
    storedQs.forEach(mergeOne)
  }

  const questions = orderQuestionMapByInstrument(byId, order0)
  return {
    ...base,
    questions,
    instance_id: base?.instance_id || stored?.instance_id,
    last_updated: stored?.last_updated || base?.last_updated,
  }
}

/**
 * Final Review screen: all answered questions in instrument order (GOLD-11478).
 */
export const buildOrderedReviewQuestions = ({
  webSurvey,
  submissionSurvey,
  storedInstrument,
  inReviewIdMap = {},
}) => {
  const byId = new Map()
  const mergeOne = (q) => {
    if (!q?.id) return
    const prev = byId.get(q.id)
    if (!prev) {
      byId.set(q.id, { ...q })
      return
    }
    const pLen = Array.isArray(prev.answer) ? prev.answer.length : 0
    const nLen = Array.isArray(q.answer) ? q.answer.length : 0
    byId.set(q.id, {
      ...prev,
      ...q,
      answer: nLen >= pLen ? (q.answer ?? prev.answer) : prev.answer,
    })
  }
    ; (submissionSurvey?.questions || []).forEach(mergeOne)
    ; (storedInstrument?.questions || []).forEach(mergeOne)

  const order0 = webSurvey?.order?.[0] || []
  const defs = webSurvey?.questions || {}
  const out = []
  for (const qid of order0) {
    const q = byId.get(qid)
    if (!q) continue
    if (q.type === 'introduction' || q.type === 'prompt') continue
    if (inReviewIdMap[qid] === false) continue
    const def = defs[qid]
    const merged =
      def && typeof def === 'object'
        ? {
          ...def,
          ...q,
          type: q.type || def.type,
          attributes: {
            ...(def.attributes || {}),
            ...(q.attributes || {}),
          },
          answer: Array.isArray(q.answer) ? q.answer : [],
        }
        : q
    out.push(merged)
  }
  return out
}

export const checkAndAddCompletedQuestion = (
  questions,
  question = {},
  order
) => {
  if (!questions) return []
  const questionsArr = questions.map((_question) => {
    return _question.id
  })
  const { id } = question
  const questionIndex = questionsArr.indexOf(id)

  // If question doesn't exist, add it to the end
  if (questionIndex === -1) return [...questions, question]

  // If question exists, update it in place WITHOUT truncating other questions
  const newQuestions = [...questions]
  newQuestions[questionIndex] = question
  return newQuestions
}

export const checkAndAddQuestion = (
  questionArr,
  questionIds = [],
  order,
  justCompletedQuestion = {}
) => {
  // When user goes back then clicks Next again, the next question may already
  // be in the survey. Do not truncate in that case to avoid losing questions
  // (and their answers) that were already ahead (GOLD-11360).
  if (questionIds.some((id) => questionArr.indexOf(id) !== -1)) {
    return questionArr
  }
  const questionIndex = questionArr.indexOf(justCompletedQuestion.id)
  const newQuestionArr = questionArr.slice(0, questionIndex + 1)
  return [...newQuestionArr, ...questionIds]
}

// skip logic for survey questions

const getHasLogic = (webSurvey, nextQuestionId) => {
  // Checks if the next question has any skip logic
  const { questions } = webSurvey
  const nextQuestion = questions[nextQuestionId]
  if (!nextQuestion) return false
  const { logic } = nextQuestion

  const logicArr = Object.keys(logic || {})
  return logicArr.length !== 0
}

/**
 * Whether advancing to the next question / submit should run (e.g. false on sibling pages until the shared footer completes).
 * Consent instruments use last-question semantics instead of sibling gating.
 */
export const getShouldRenderNextQuestion = ({
  isConsent = false,
  isLastQuestion = false,
  isSibling = false,
} = {}) => (isConsent ? !isLastQuestion : !isSibling)

/** Collect stored answer tokens used by show_if `is_selected` / `is_not_selected`. */
const appendAnswerValuesForLogic = (target, answer, authoredQuestion) => {
  if (!answer || typeof answer !== 'object') return
  const { choice_id: choiceId, id: answerId, value } = answer
  if (choiceId != null && choiceId !== '') {
    target.push(choiceId)
  }
  if (answerId != null && answerId !== '' && answerId !== choiceId) {
    target.push(answerId)
  }
  if (value !== undefined && value !== null) {
    target.push(value)
  }
  const resolved = resolveChoiceIdFromStoredAnswer(
    authoredQuestion?.choices,
    answer,
  )
  if (resolved) target.push(resolved)
}

const createAnswerMap = (
  submissionSurvey,
  currentAnswer = {},
  questions = {},
) => {
  const answerMap = {}

  ;(submissionSurvey?.questions || []).forEach((_question) => {
    if (_question?.type === 'matrix') {
      answerMap[_question.id] = {}
      ;(_question.answer || []).forEach((matrixQuestion) => {
        const matrixAnswerArray = []
        ;(matrixQuestion?.answer || []).forEach((answer) => {
          appendAnswerValuesForLogic(
            matrixAnswerArray,
            answer,
            questions[_question.id],
          )
        })
        answerMap[_question.id][matrixQuestion?.question] = matrixAnswerArray
      })
    } else {
      answerMap[_question.id] = []
      ;(_question.answer || []).forEach((answer) => {
        appendAnswerValuesForLogic(
          answerMap[_question.id],
          answer,
          questions[_question.id],
        )
      })
    }
  })

  const answerIDs = Object.keys(currentAnswer || {})
  answerIDs.forEach((answerID) => {
    let isMatrix = false
    const newAnswerObj = {}
    const newAnswerArr = []

    const questionAnswer = currentAnswer[answerID]
    if (Array.isArray(questionAnswer)) {
      questionAnswer.forEach((chosenAnswer) => {
        const { answer, question: subQuestionId, value, choice_id: choiceId } =
          chosenAnswer || {}
        if (
          (value !== undefined && value !== null) ||
          (choiceId != null && choiceId !== '')
        ) {
          appendAnswerValuesForLogic(
            newAnswerArr,
            chosenAnswer,
            questions[answerID],
          )
          return
        }
        isMatrix = true
        if (subQuestionId && Array.isArray(answer)) {
          newAnswerObj[subQuestionId] = []
          answer.forEach((subQuestionAnswer) => {
            appendAnswerValuesForLogic(
              newAnswerObj[subQuestionId],
              subQuestionAnswer,
              questions[answerID],
            )
          })
        }
      })
    }

    Object.assign(answerMap, {
      [answerID]: isMatrix ? newAnswerObj : newAnswerArr,
    })
  })

  return answerMap
}

const getCompleteBool = (nextQuestion, questions, answerMap) => {
  const { logic } = nextQuestion
  const { show_if } = logic
  const { terms, operator } = show_if

  const anyBool = operator === 'or'
  let completeBool = false

  const checkTermsAndComplete = (_terms, any) => {
    for (const term of _terms) {
      const { value, comparator, sub_question_id } = term

      let questionId
      let questionType

      if (comparator === 'boolean_combine') {
        const __terms = term.terms
        const __operator = term.operator
        const __any = __operator === 'or'
        checkTermsAndComplete(__terms, __any)
      } else {
        questionId = term.question_id
        questionType = questions[questionId]?.type
      }

      const answerMapValueIsArr = Array.isArray(answerMap[questionId])

      if (comparator === 'is_selected') {
        if (!answerMap[questionId]) {
          completeBool = false
          if (!any) break
        } else if (answerMapValueIsArr && answerMap[questionId]?.includes(value)) {
          completeBool = true
          if (any) break
        } else if (!answerMapValueIsArr) {
          if (answerMap[questionId]?.[sub_question_id]?.includes(value)) {
            completeBool = true
            if (any) break
          } else {
            completeBool = any ? completeBool : false
            if (!any) break
          }
        } else {
          completeBool = any ? completeBool : false
          if (!any) break
        }
      }

      if (comparator === 'is_not_selected') {
        if (!answerMap[questionId]) {
          completeBool = false
          if (!any) break
        } else if (
          answerMapValueIsArr &&
          !answerMap[questionId]?.includes(value)
        ) {
          completeBool = true
          if (any) break
        } else if (!answerMapValueIsArr) {
          if (!answerMap[questionId]?.[sub_question_id]?.includes(value)) {
            completeBool = true
            if (any) break
          } else {
            completeBool = any ? completeBool : false
            if (!any) break
          }
        } else {
          completeBool = any ? completeBool : false
          if (!any) break
        }
      }

      if (comparator === 'is_answered') {
        if (!answerMap[questionId]) {
          completeBool = false
          if (!any) break
        } else if (answerMapValueIsArr && answerMap[questionId].length > 0) {
          completeBool = true
          if (any) break
        } else if (!answerMapValueIsArr) {
          if ((answerMap[questionId]?.[sub_question_id] || []).length > 0) {
            completeBool = true
            if (any) break
          } else {
            completeBool = any ? completeBool : false
            if (!any) break
          }
        } else {
          completeBool = any ? completeBool : false
          if (!any) break
        }
      }
      if (comparator === 'is_not_answered') {
        if (!answerMap[questionId]) {
          completeBool = false
          if (!any) break
        } else if (answerMapValueIsArr && answerMap[questionId].length === 0) {
          completeBool = true
          if (any) break
        } else if (!answerMapValueIsArr) {
          if ((answerMap[questionId]?.[sub_question_id] || []).length === 0) {
            completeBool = true
            if (any) break
          } else {
            completeBool = any ? completeBool : false
            if (!any) break
          }
        } else {
          completeBool = any ? completeBool : false
          if (!any) break
        }
      }

      if (
        comparator === '=' ||
        comparator === '!=' ||
        comparator === '>=' ||
        comparator === '>' ||
        comparator === '<=' ||
        comparator === '<'
      ) {
        let _case

        const isDateOrTimeType =
          questionType === 'date' ||
          questionType === 'datetime' ||
          questionType === 'time'

        if (isDateOrTimeType) {
          const isTime = questionType === 'time'
          const logicVal = isTime
            ? getMomentTime(value)
            : getUnixTimestampSeconds(value)

          if (!answerMap[questionId]) {
            completeBool = false
            if (!any) break
          } else if (answerMap[questionId]) {
            const answerVal = isTime
              ? getMomentTime(answerMap[questionId][0])
              : getUnixTimestampSeconds(answerMap[questionId][0])

            if (comparator === '=') {
              _case = isTime ? answerVal.isSame(logicVal) : answerVal === logicVal
            }
            if (comparator === '!=') {
              _case = isTime ? !answerVal.isSame(logicVal) : answerVal !== logicVal
            }
            if (comparator === '>=') {
              _case = isTime
                ? answerVal.isSame(logicVal) || answerVal.isAfter(logicVal)
                : answerVal >= logicVal
            }
            if (comparator === '>') {
              _case = isTime ? answerVal.isAfter(logicVal) : answerVal > logicVal
            }
            if (comparator === '<=') {
              _case = isTime
                ? answerVal.isSame(logicVal) || answerVal.isBefore(logicVal)
                : answerVal <= logicVal
            }
            if (comparator === '<') {
              _case = isTime ? answerVal.isBefore(logicVal) : answerVal < logicVal
            }

            if (_case) {
              completeBool = true
              if (any) break
            } else {
              completeBool = any ? completeBool : false
              if (!any) break
            }
          }
        } else if (!isDateOrTimeType) {
          if (!answerMap[questionId]) {
            completeBool = false
            if (!any) break
          } else if (answerMap[questionId][0]) {
            if (comparator === '=') _case = answerMap[questionId][0] === value
            if (comparator === '!=') _case = answerMap[questionId][0] !== value
            if (comparator === '>=') _case = answerMap[questionId][0] >= value
            if (comparator === '>') _case = answerMap[questionId][0] > value
            if (comparator === '<=') _case = answerMap[questionId][0] <= value
            if (comparator === '<') _case = answerMap[questionId][0] < value

            if (_case) {
              completeBool = true
              if (any) break
            } else {
              completeBool = any ? completeBool : false
              if (!any) break
            }
          }
        }
      }
      if (!completeBool && !any) break
      if (completeBool && any) break
    }
  }

  checkTermsAndComplete(terms, anyBool)
  return completeBool
}

export const checkLogicAndComplete = (
  webSurvey,
  currentId,
  submissionSurvey,
  currentAnswer,
  nextQId,
  renderNextQ,
  completeQuestion,
  submission,
  reviewFunctionalitySupported = false,
  shouldRenderNextQ = true,
) => {
  const { questions: surveyQuestions, order } = webSurvey
  completeQuestion(submission, order[0])

  const nextQuestionId = nextQId(currentId)
  const nextQuestion = surveyQuestions[nextQuestionId]

  if (nextQuestionId === 'submit-id') {
    if (!shouldRenderNextQ) return null
    return renderNextQ(currentId, submission)
  }
  if (nextQuestionId === 'review' && reviewFunctionalitySupported) {
    if (!shouldRenderNextQ) return null
    return renderNextQ(currentId, submission)
  }
  if (nextQuestionId === 'resume-review') {
    if (!shouldRenderNextQ) return null
    return renderNextQ(currentId, submission)
  }

  const hasLogic = getHasLogic(webSurvey, nextQuestionId)
  if (!hasLogic) {
    return shouldRenderNextQ ? renderNextQ(currentId, submission) : null
  }
  if (!nextQuestion) {
    return shouldRenderNextQ ? renderNextQ(currentId, submission) : null
  }

  const answerMap = createAnswerMap(
    submissionSurvey,
    currentAnswer,
    surveyQuestions,
  )

  const complete = () => {
    return checkLogicAndComplete(
      webSurvey,
      nextQId(currentId),
      submissionSurvey,
      currentAnswer,
      nextQId,
      renderNextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  const completeBool = getCompleteBool(nextQuestion, surveyQuestions, answerMap)

  if (completeBool && shouldRenderNextQ)
    return renderNextQ(currentId, submission)
  return complete()
}

export const parseVASValue = (nextVal, prevVal) => {
  const num = Number(nextVal)
  if (Number.isNaN(num) || num < 0 || num > 100 || !num) return prevVal
  return Math.abs(Math.round(num)).toString()
}

export const getShouldDisableSiblingSubmission = (
  webSurvey,
  submissionSurvey,
  siblingOrder,
) => {
  if (!Array.isArray(siblingOrder) || siblingOrder.length === 0) return false
  const { questions } = submissionSurvey
  return siblingOrder.some((questionId) => {
    const question = webSurvey?.questions[questionId]
    if (question?.type === QUESTION_TYPES.introduction) {
      return false
    }
    const submissionQuestion = questions.filter((q) => q.id === questionId)[0]
    if (!submissionQuestion) return question?.attributes?.required
    const { required, state } = submissionQuestion
    if (state === RESPONSE_STATES.answered) {
      return false
    }
    return required
  })
}

export const findSiblingsOrder = (
  webSurvey,
  submissionSurvey,
  siblingId,
  currentQuestionId,
) => {
  const siblingsMap = webSurvey?.siblings
  if (!siblingsMap || !siblingsMap[siblingId]) {
    return currentQuestionId != null ? [currentQuestionId] : []
  }
  // `siblings[siblingId]` can include authoring mistakes (e.g. introduction or
  // other ids not in this group). Only keep questions that declare this group
  // via `sibling_id`, and never treat introduction as a sibling row.
  const originalOrder = siblingsMap[siblingId]
  const order0 = webSurvey?.order?.[0] || []
  const orderIndex = new Map(order0.map((qid, idx) => [qid, idx]))

  const siblingsToRender = originalOrder
    .filter((questionId) => {
      const question = webSurvey.questions[questionId]
      if (!question) return false
      if (question.type === QUESTION_TYPES.introduction) return false
      if (!WEB_SURVEY_RENDERABLE_QUESTION_TYPES.has(question.type)) return false
      if (question.sibling_id !== siblingId) return false
      const hasLogic = getHasLogic(webSurvey, questionId)
      if (!hasLogic) return true
      const answerMap = createAnswerMap(
        submissionSurvey,
        {},
        webSurvey.questions,
      )
      return getCompleteBool(question, webSurvey.questions, answerMap)
    })
    .sort((a, b) => {
      const ia = orderIndex.has(a) ? orderIndex.get(a) : Number.MAX_SAFE_INTEGER
      const ib = orderIndex.has(b) ? orderIndex.get(b) : Number.MAX_SAFE_INTEGER
      return ia - ib
    })

  if (siblingsToRender.length) return siblingsToRender
  return currentQuestionId != null ? [currentQuestionId] : []
}
